import { Alert, Button, Typography } from '@mui/material';
import { find, map } from 'lodash';
import moment from 'moment';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { payinProviderStatisticsApi, shopsApi } from 'api';
import { QueryFilters } from 'components';
import {
  FilterDefinitionType,
  PayinProviderScope,
  PayinStatisticsGroupBy,
  QueryKey,
} from 'enums';
import {
  usePartialQuery,
  useQueryFilters,
  useShopsQuery,
  useUser,
  useUserContext,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import {
  FilterDefinition,
  PayinProviderStatisticFilters,
  QueryParams,
} from 'types';
import { formUtils, payinProviderStatisticsUtils } from 'utils';

import { CancelReasonsTable } from './CancelReasonsTable';
import { ConversionFunnelTable } from './ConversionFunnelTable';
import { IntegrationHealthBlock } from './IntegrationHealthBlock';
import { LossPointsBlock } from './LossPointsBlock';
import { NotAssignedReasonsTable } from './NotAssignedReasonsTable';
import { ProviderConversionTable } from './ProviderConversionTable';
import { ProviderErrorsTable } from './ProviderErrorsTable';
import { TimingsBlock } from './TimingsBlock';

// URL pagination is shared by the whole tab, so tables skip it and fetch the aggregated groups at once
const AGGREGATED_ROWS_TAKE = 100;

// Must match PAYIN_STATISTICS_MAX_PERIOD_DAYS on the backend
const MAX_PERIOD_DAYS = 30;

export const ProviderConversionTab: React.FC = () => {
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.statistics.provider_conversion',
  });

  const { apiRole } = useUser();
  const { p2pProviders } = useUserContext();

  const { filters, setFilters } =
    useQueryFilters<PayinProviderStatisticFilters>({
      from: moment().subtract(1, 'w').startOf('d').toISOString(),
    });

  // The filters param is shared with sibling tabs, which allow ranges wider than the backend accepts
  const isPeriodTooWide = useMemo(() => {
    if (!filters?.from) {
      return false;
    }
    const to = filters.to ? moment(filters.to) : moment();
    return to.diff(moment(filters.from), 'days', true) > MAX_PERIOD_DAYS;
  }, [filters]);

  // Firing six requests that the backend is bound to reject would replace the explanation above with
  // six load errors
  const queryOptions = useMemo(
    () => ({ paginated: false, enabled: !isPeriodTooWide }),
    [isPeriodTooWide],
  );

  const handleResetPeriod = useCallback(() => {
    setFilters({
      ...filters,
      from: moment().subtract(1, 'w').startOf('d').toISOString(),
      to: undefined,
    } as PayinProviderStatisticFilters);
  }, [filters, setFilters]);

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(apiRole),
  );

  const getConversion = useCallback(
    (params: QueryParams) =>
      payinProviderStatisticsApi.getConversion({
        ...params,
        page: 1,
        take: AGGREGATED_ROWS_TAKE,
      }),
    [],
  );

  const getErrors = useCallback(
    (params: QueryParams) =>
      payinProviderStatisticsApi.getErrors({
        ...params,
        page: 1,
        take: AGGREGATED_ROWS_TAKE,
      }),
    [],
  );

  const queryResultConversion = usePartialQuery(
    QueryKey.PayinProviderConversion,
    getConversion,
    queryOptions,
  );

  const queryResultErrors = usePartialQuery(
    QueryKey.PayinProviderErrors,
    getErrors,
    queryOptions,
  );

  const queryResultNotAssignedReasons = usePartialQuery(
    QueryKey.PayinProviderNotAssignedReasons,
    payinProviderStatisticsApi.getNotAssignedReasons,
    queryOptions,
  );

  const queryResultCancelReasons = usePartialQuery(
    QueryKey.PayinProviderCancelReasons,
    payinProviderStatisticsApi.getCancelReasons,
    queryOptions,
  );

  const queryResultLossPoints = usePartialQuery(
    QueryKey.PayinProviderLossPoints,
    payinProviderStatisticsApi.getLossPoints,
    queryOptions,
  );

  const queryResultTimings = usePartialQuery(
    QueryKey.PayinProviderTimings,
    payinProviderStatisticsApi.getTimings,
    queryOptions,
  );

  const queryResultIntegrationHealth = usePartialQuery(
    QueryKey.PayinProviderIntegrationHealth,
    payinProviderStatisticsApi.getIntegrationHealth,
    queryOptions,
  );

  const groupByOptions = useMemo(
    () =>
      map(PayinStatisticsGroupBy, (groupBy) => ({
        value: groupBy,
        label: payinProviderStatisticsUtils.getGroupByLabel(groupBy),
      })),
    [],
  );

  const providerScopeOptions = useMemo(
    () =>
      map(PayinProviderScope, (scope) => ({
        value: scope,
        label: t(`provider_scope.${scope}`),
      })),
    [t],
  );

  const getGroupByName = useCallback(
    (value: string) =>
      payinProviderStatisticsUtils.getGroupByLabel(
        value as PayinStatisticsGroupBy,
      ),
    [],
  );

  const getProviderScopeName = useCallback(
    (value: string) => t(`provider_scope.${value as PayinProviderScope}`),
    [t],
  );

  const getProviderName = useCallback(
    (value: string) => find(p2pProviders, { id: value })?.name,
    [p2pProviders],
  );

  const filtersDefinitions: FilterDefinition<PayinProviderStatisticFilters>[] =
    useMemo(
      () => [
        {
          label: tCommon('filters.shop'),
          name: 'shopId',
          type: FilterDefinitionType.Shop,
          shops: queryResultShops.data,
        },
        {
          label: tCommon('filters.p2p_provider'),
          name: 'p2pProviderId',
          type: FilterDefinitionType.Select,
          options: formUtils.getOptions(p2pProviders),
          getDisplayName: getProviderName,
        },
        {
          label: t('group_by.label'),
          name: 'groupBy',
          type: FilterDefinitionType.Select,
          options: groupByOptions,
          getDisplayName: getGroupByName,
        },
        {
          label: t('provider_scope.label'),
          name: 'providerScope',
          type: FilterDefinitionType.Select,
          options: providerScopeOptions,
          getDisplayName: getProviderScopeName,
        },
      ],
      [
        t,
        tCommon,
        queryResultShops.data,
        p2pProviders,
        getProviderName,
        groupByOptions,
        providerScopeOptions,
        getGroupByName,
        getProviderScopeName,
      ],
    );

  return (
    <Fragment>
      <QueryFilters withCommon filtersDefinitions={filtersDefinitions} />
      {isPeriodTooWide && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleResetPeriod}>
              {t('period_limit_action')}
            </Button>
          }
        >
          {t('period_limit', { days: MAX_PERIOD_DAYS })}
        </Alert>
      )}
      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
        {t('funnel.title')}
      </Typography>
      <ConversionFunnelTable queryResult={queryResultConversion} />
      <Typography variant="h6" sx={{ mt: 6, mb: 2 }}>
        {t('providers.title')}
      </Typography>
      <ProviderConversionTable queryResult={queryResultConversion} />
      <Typography variant="h6" sx={{ mt: 6, mb: 2 }}>
        {t('not_assigned.title')}
      </Typography>
      <NotAssignedReasonsTable queryResult={queryResultNotAssignedReasons} />
      <Typography variant="h6" sx={{ mt: 6, mb: 2 }}>
        {t('errors.title')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
        {t('errors.description')}
      </Typography>
      <ProviderErrorsTable queryResult={queryResultErrors} />
      <Typography variant="h6" sx={{ mt: 6, mb: 2 }}>
        {t('cancel_reasons.title')}
      </Typography>
      <CancelReasonsTable queryResult={queryResultCancelReasons} />
      <LossPointsBlock queryResult={queryResultLossPoints} />
      <TimingsBlock queryResult={queryResultTimings} />
      <IntegrationHealthBlock queryResult={queryResultIntegrationHealth} />
    </Fragment>
  );
};
